import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

/**
 * In-app replacement for `window.confirm()` — a centered modal card with
 * backdrop, styled to match the rest of the PWA's design language. Exposed
 * via a `useConfirm()` hook that returns a Promise<boolean> so call sites
 * stay flat:
 *
 *   const confirm = useConfirm();
 *   const ok = await confirm({
 *     title: 'Convert to Sale',
 *     message: 'Reserve stock from oldest batches?',
 *     confirmLabel: 'Convert',
 *     confirmVariant: 'primary',
 *   });
 *   if (!ok) return;
 *
 * Wrap `<App />` in `<ConfirmProvider>` so the hook is available everywhere.
 */

const ConfirmContext = createContext(null);

export function useConfirm() {
  const fn = useContext(ConfirmContext);
  if (!fn) {
    throw new Error('useConfirm must be used within <ConfirmProvider>');
  }
  return fn;
}

export function ConfirmProvider({ children }) {
  // Single dialog state — opening another while one's already open is rare
  // enough that we just queue by overwriting (last one wins). Resolver is
  // wrapped in a setter-fn because resolvers are themselves functions and
  // useState would otherwise call them immediately.
  const [config, setConfig] = useState(null);
  const resolverRef = useRef(null);

  const confirm = useCallback((opts) => {
    return new Promise((resolve) => {
      // Resolve any prior pending confirm with `false` so we don't leak
      // promise leaks if a caller fires two confirms back-to-back.
      if (resolverRef.current) resolverRef.current(false);
      resolverRef.current = resolve;
      setConfig(opts);
    });
  }, []);

  const close = useCallback((result) => {
    if (resolverRef.current) {
      resolverRef.current(result);
      resolverRef.current = null;
    }
    setConfig(null);
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {config && (
        <ConfirmDialog
          {...config}
          onConfirm={() => close(true)}
          onCancel={() => close(false)}
        />
      )}
    </ConfirmContext.Provider>
  );
}

const VARIANT_CLASSES = {
  primary: 'bg-amber-500 hover:bg-amber-600 active:bg-amber-600 text-white border-amber-500',
  danger:  'bg-red-600 hover:bg-red-700 active:bg-red-700 text-white border-red-600',
  success: 'bg-green-600 hover:bg-green-700 active:bg-green-700 text-white border-green-600',
};

function ConfirmDialog({
  title,
  message,
  confirmLabel  = 'Confirm',
  cancelLabel   = 'Cancel',
  confirmVariant = 'primary',
  onConfirm,
  onCancel,
}) {
  const dialogRef = useRef(null);

  // Auto-focus the confirm button on open so keyboard / screenreader users
  // land in the right place. Also close on Escape.
  useEffect(() => {
    const confirmBtn = dialogRef.current?.querySelector('[data-confirm]');
    confirmBtn?.focus();

    const onKey = (e) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const variantCls = VARIANT_CLASSES[confirmVariant] ?? VARIANT_CLASSES.primary;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-title"
      onClick={onCancel}
    >
      <div
        ref={dialogRef}
        // Stop propagation so taps inside the card don't bubble to the
        // backdrop's onClick (which would close the dialog).
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-sm bg-white rounded-card shadow-2xl p-5"
        // Slight slide-up animation gives the dialog a touch of life on
        // mobile without requiring a CSS animation library.
        style={{ animation: 'confirm-pop 140ms ease-out' }}
      >
        <h2
          id="confirm-title"
          className="text-title font-bold text-gray-900 mb-2"
        >
          {title}
        </h2>

        {message && (
          <p className="text-small text-gray-700 mb-5 whitespace-pre-line">
            {message}
          </p>
        )}

        <div className="flex gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 h-11 rounded-md border border-gray-300 bg-white text-gray-700 font-medium hover:bg-gray-50 active:bg-gray-100 focus:outline-none"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            data-confirm
            onClick={onConfirm}
            className={`flex-1 h-11 rounded-md border font-semibold focus:outline-none ${variantCls}`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>

      {/* Inline keyframes for the pop-in. Scoped to this component so we
          don't pollute global CSS for a single-use animation. */}
      <style>{`
        @keyframes confirm-pop {
          from { opacity: 0; transform: translateY(8px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0)   scale(1);    }
        }
      `}</style>
    </div>
  );
}
